import logger from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Bell, Trash2, Eye, EyeOff, CheckCircle, XCircle, Info } from 'lucide-react';
import { useAuth } from './AuthContext';
import api from '../utils/api';

const Notifications = ({ onNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { currentUser } = useAuth();

  const fetchNotifications = useCallback(async () => {
    if (!currentUser) return;

    setIsLoading(true);
    try {
      const response = await api.get(`/notifications/user/${currentUser.id}`);
      // Transform dates from ISO strings to Date objects
      const transformedData = response.data.map(n => ({
        ...n,
        date: new Date(n.created_at),
        read: n.read
      }));
      setNotifications(transformedData);
    } catch (error) {
      logger.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const toggleReadStatus = async (id, currentReadStatus) => {
    try {
      const endpoint = currentReadStatus
        ? `/notifications/${id}/unread`
        : `/notifications/${id}/read`;

      await api.put(endpoint);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: !currentReadStatus } : n)
      );
    } catch (error) {
      logger.error('Error toggling read status:', error);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      logger.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'reservation_approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'reservation_rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'reservation_created':
        return <Bell className="w-5 h-5 text-blue-600" />;
      case 'payment_received':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      default:
        return <Info className="w-5 h-5 text-gray-600" />;
    }
  };

  const handleNotificationClick = async (notification) => {
    // Marcar como leída si no lo está
    if (!notification.read) {
      await toggleReadStatus(notification.id, notification.read);
    }

    // Navegar al módulo correcto según el rol del usuario
    if (!onNavigate) return;

    // Determinar a qué módulo ir según el rol
    const userRole = currentUser?.role;

    // El ID de la reserva puede estar en diferentes lugares según el tipo de notificación
    // Para solicitudes de cambio: metadata.reservation_id
    // Para otras notificaciones: reference_id o metadata.reservation_id
    const reservationId = notification.metadata?.reservation_id || notification.reference_id;

    // Determinar la sección a abrir según el tipo de notificación
    let section = null;
    const notificationType = notification.type;

    // Para notificaciones de cambios, abrir en la sección de actividades
    const isChangeRequest = notificationType?.includes('change_request') ||
                           notificationType?.includes('solicitud') ||
                           notification.message?.toLowerCase().includes('solicitud de cambio') ||
                           notification.title?.toLowerCase().includes('solicitud de cambio');

    if (userRole === 'asesor') {
      // Los asesores van al módulo de Reservas
      onNavigate('reservations', reservationId, null);
    } else if (userRole === 'gestor' || userRole === 'administrador') {
      // Gestores y administradores van al módulo de Gestión
      // Si es una notificación de solicitud de cambio, abrir en la sección de actividades
      if (isChangeRequest) {
        section = 'history'; // Sección de actividades y cambios
      }
      onNavigate('gestion', reservationId, section);
    }
  };

  const getNotificationBgColor = (notification) => {
    if (notification.read) return 'bg-white';

    switch (notification.type) {
      case 'reservation_approved':
        return 'bg-green-50';
      case 'reservation_rejected':
        return 'bg-red-50';
      case 'reservation_created':
        return 'bg-blue-50';
      default:
        return 'bg-blue-50';
    }
  };

  const getNotificationBorderColor = (notification) => {
    if (notification.read) return '';

    switch (notification.type) {
      case 'reservation_approved':
        return 'border border-green-200';
      case 'reservation_rejected':
        return 'border border-red-200';
      case 'reservation_created':
        return 'border border-blue-200';
      default:
        return 'border border-blue-200';
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600">Cargando notificaciones...</p>
      </div>
    );
  }

  return (
    <motion.div
      className="p-6 space-y-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Main content for notifications page */}
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Todas las Notificaciones</h3>
          {unreadCount > 0 && (
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
              {unreadCount} sin leer
            </span>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No tienes notificaciones</p>
            <p className="text-gray-400 mt-2">Las notificaciones sobre tus reservas aparecerán aquí</p>
          </div>
        ) : (
          <ul className="space-y-4">
            {notifications.map(n => (
              <motion.li
                key={n.id}
                className={`p-4 rounded-lg shadow-sm flex items-start justify-between ${getNotificationBgColor(n)} ${getNotificationBorderColor(n)}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div
                  className="flex items-start gap-3 flex-1 cursor-pointer"
                  onClick={() => handleNotificationClick(n)}
                >
                  {getNotificationIcon(n.type)}
                  <div className="flex-1">
                    <p className={`font-semibold ${n.read ? 'text-gray-800' : 'text-blue-800'}`}>
                      {n.title}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">{n.message}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {new Date(n.date).toLocaleString('es-ES', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <motion.button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleReadStatus(n.id, n.read);
                    }}
                    className="p-2 rounded-full text-gray-500 hover:text-blue-600 hover:bg-gray-100"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title={n.read ? "Marcar como no leída" : "Marcar como leída"}
                  >
                    {n.read ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </motion.button>
                  <motion.button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(n.id);
                    }}
                    className="p-2 rounded-full text-gray-500 hover:text-red-600 hover:bg-gray-100"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Eliminar notificación"
                  >
                    <Trash2 className="w-5 h-5" />
                  </motion.button>
                </div>
              </motion.li>
            ))}
          </ul>
        )}
      </div>
    </motion.div>
  );
};

export default Notifications;
